<template>
  <div class="journey-builder">
    <b-row class="jb-body">
      <!-- left: header + rail + editor, so the header no longer spans the preview -->
      <b-col cols="12" lg="8">
    <!-- header -->
    <b-card no-body class="jb-head">
      <div class="jb-head__row">
        <b-button variant="flat-secondary" size="sm" class="btn-icon" :to="'/journey'">
          <feather-icon icon="ArrowLeftIcon" size="16"/>
        </b-button>
        <b-form-input v-model="journey.name" class="jb-head__name" :placeholder="msg('journeyname')"/>
        <b-form-checkbox switch v-model="journey.active" class="jb-head__active">
          {{ journey.active ? msg('Active') : msg('Inactive') }}
        </b-form-checkbox>
        <b-button variant="primary" :disabled="saving || !canSave" @click="save">
          <b-spinner v-if="saving" small class="mr-25"/>
          <feather-icon v-else icon="SaveIcon" size="15" class="mr-25"/>
          {{ msg('save') }}
        </b-button>
      </div>

      <div class="jb-head__row jb-head__row--target">
        <journey-target-picker v-model="target" class="jb-head__target"/>
      </div>

      <b-alert v-if="usedTemplate" show variant="primary" class="jb-alert" dismissible
               @dismissed="usedTemplate = false">
        <feather-icon icon="ZapIcon" size="15" class="mr-50"/>
        {{ msg('startertemplatehint') }}
        <b-button variant="flat-primary" size="sm" class="ml-50" @click="startBlank">
          {{ msg('startblank') }}
        </b-button>
      </b-alert>
      <b-alert v-if="conflicts.length" show variant="warning" class="jb-alert">
        <feather-icon icon="AlertTriangleIcon" size="15" class="mr-50"/>
        {{ msg('journeyconflicthint') }}: {{ conflicts.map(c => c.name).join(', ') }}
      </b-alert>
      <b-alert v-if="duplicateKeys.length" show variant="danger" class="jb-alert">
        <feather-icon icon="AlertCircleIcon" size="15" class="mr-50"/>
        {{ msg('duplicatedkey') }}: {{ duplicateKeys.join(', ') }}
      </b-alert>
    </b-card>

    <b-row>
      <!-- steps -->
      <b-col cols="12" lg="4">
        <b-card no-body class="jb-pane">
          <div class="jb-pane__title">{{ msg('steps') }}</div>
          <div class="jb-pane__body">
            <journey-step-rail
                v-model="journey.steps"
                :selected="selected"
                :duplicate-keys="duplicateKeys"
                @select="selected = $event"
            />
          </div>
        </b-card>
      </b-col>

      <!-- editor -->
      <b-col cols="12" lg="8">
        <b-card no-body class="jb-pane">
          <div class="jb-pane__title jb-pane__title--bar">
            <span>{{ selectedTitle }}</span>
            <!-- the settings pane renders only when NOTHING is selected, so
                 clearing the selection is the only route back to it -->
            <b-button
                v-if="selected"
                size="sm"
                variant="flat-primary"
                class="jb-back"
                @click="selected = null"
            >
              <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25"/>
              {{ msg('backtosettings') }}
            </b-button>
          </div>
          <div class="jb-pane__body">
            <!-- step editor -->
            <template v-if="selected && selected.type === 'step' && selectedStep">
              <b-form-group :label="msg('steptitle')">
                <b-form-input :value="selectedStep.title" @input="patchStep({title: $event})"/>
              </b-form-group>
              <b-form-group :label="msg('stepsubtitle')">
                <b-form-input :value="selectedStep.subtitle" @input="patchStep({subtitle: $event})"/>
              </b-form-group>
              <b-form-group :label="msg('icon')">
                <b-form-input :value="selectedStep.icon" placeholder="CalendarIcon"
                              @input="patchStep({icon: $event})"/>
              </b-form-group>
              <journey-condition-editor
                  :value="selectedStep.condition"
                  :available-questions="questionsBefore(selected)"
                  :all-questions="allQuestions"
                  @input="patchStep({condition: $event})"
              />
            </template>

            <!-- question editor -->
            <journey-question-editor
                v-else-if="selected && selected.type === 'question' && selectedQuestion"
                :value="selectedQuestion"
                :features="filteredFeatures"
                :preceding-questions="questionsBefore(selected)"
                :all-questions="allQuestions"
                :duplicate-key="duplicateKeys.includes(selectedQuestion.key)"
                @input="patchQuestion($event)"
            />

            <!-- settings (nothing selected) -->
            <template v-else>
              <div class="jb-settings-title">{{ msg('welcomescreen') }}</div>
              <b-form-group :label="msg('welcometitle')">
                <b-form-input v-model="settings.welcomeTitle"/>
              </b-form-group>
              <b-form-group :label="msg('welcometext')">
                <b-form-textarea v-model="settings.welcomeText" rows="2"/>
              </b-form-group>

              <div class="jb-settings-title">{{ msg('thankyouscreen') }}</div>
              <b-form-group :label="msg('thankyoutitle')">
                <b-form-input v-model="settings.thankYouTitle"/>
              </b-form-group>
              <b-form-group :label="msg('thankyoutext')">
                <b-form-textarea v-model="settings.thankYouText" rows="2"/>
              </b-form-group>

              <div class="jb-settings-title">{{ msg('Settings') }}</div>
              <b-row>
                <b-col cols="6" md="4">
                  <b-form-group>
                    <template #label>{{ msg('showprogressbar') }}<info-hint :text="msg('info_showprogressbar')"/></template>
                    <b-form-checkbox switch v-model="settings.showProgressBar"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group>
                    <template #label>{{ msg('allowdraft') }}<info-hint :text="msg('info_allowdraft')"/></template>
                    <b-form-checkbox switch v-model="settings.allowDraft"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group>
                    <template #label>{{ msg('allowresubmit') }}<info-hint :text="msg('info_allowresubmit')"/></template>
                    <b-form-checkbox switch v-model="settings.allowResubmit"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group>
                    <template #label>{{ msg('checkoutenabled') }}<info-hint :text="msg('info_checkoutenabled')"/></template>
                    <b-form-checkbox switch v-model="settings.checkoutEnabled"/>
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="4">
                  <b-form-group>
                    <template #label>{{ msg('payimmediately') }}<info-hint :text="msg('info_payimmediately')"/></template>
                    <b-form-checkbox switch v-model="settings.payImmediately" :disabled="!settings.checkoutEnabled"/>
                    <small class="text-muted d-block">
                      {{ settings.payImmediately ? msg('payimmediately_on') : msg('payimmediately_off') }}
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <template #label>{{ msg('submitlabel') }}<info-hint :text="msg('info_submitlabel')"/></template>
                    <b-form-input v-model="settings.submitLabel"/>
                  </b-form-group>
                </b-col>
              </b-row>
              <small class="text-muted">{{ msg('journeysettingshint') }}</small>
            </template>
          </div>
        </b-card>
      </b-col>

    </b-row>
      </b-col>

      <!-- right: the phone, full height from the very top of the page -->
      <b-col cols="12" lg="4">
        <journey-preview
            :journey="journey"
            :products="filteredFeatures"
            :focus-step-id="focusStepId"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import JourneyStepRail from '@/components/panels/journey/journeyStepRail.vue'
import JourneyQuestionEditor from '@/components/panels/journey/journeyQuestionEditor.vue'
import JourneyConditionEditor from '@/components/panels/journey/journeyConditionEditor.vue'
import JourneyTargetPicker from '@/components/panels/journey/journeyTargetPicker.vue'
import JourneyPreview from '@/components/panels/journey/journeyPreview.vue'
import InfoHint from '@/components/panels/journey/InfoHint.vue'

const emptyJourney = () => ({
  name: '',
  scope: 'ORGANIZATION',
  active: false,
  priority: 0,
  defaultLanguage: 'en',
  languages: ['en'],
  settings: {
    // starter copy — guest-facing, meant to be edited per hotel
    welcomeTitle: 'Welcome! Let\'s get your stay ready',
    welcomeText: 'It takes less than a minute. Tell us when you plan to arrive and what makes you '
        + 'comfortable, so everything is prepared before you walk in.',
    thankYouTitle: 'You\'re all set — thank you!',
    thankYouText: 'We have received your details and our team is getting everything ready for your '
        + 'arrival. If anything changes, simply come back to this page or write to us.',
    submitLabel: 'Finish',
    checkoutTitle: 'Make your stay even better',
    checkoutText: 'Add these to your booking now and pay securely online.',
    showProgressBar: true,
    allowDraft: true,
    allowResubmit: false,
    checkoutEnabled: true,
    // declared so Vue 2 tracks it before a journey without the field loads
    payImmediately: false,
  },
  steps: [{
    id: 'new_' + Date.now().toString(36),
    title: '',
    questions: [],
  }],
});

let uid = 0;
const nid = () => 'new_' + (uid++) + '_' + Date.now().toString(36);

/**
 * "Pre-arrival (standard)" — the booking tasks the guest site shows today as
 * alerts, in the same order the backend serves them, plus arrival details and
 * extras. Every step is editable or removable like any other.
 */
const starterSteps = () => [
  {
    id: nid(), title: 'Your details', icon: 'UserIcon',
    subtitle: 'So we can send your confirmation and invoice',
    questions: [
      {id: nid(), key: 'guest_details', type: 'GUEST_DETAILS', label: 'Confirm your contact details',
        helpText: 'Email, date of birth and company details for the invoice', size: 12, blocking: true},
    ],
  },
  {
    id: nid(), title: 'Identity & booking', icon: 'ShieldIcon',
    subtitle: 'A legal requirement before check-in',
    questions: [
      {id: nid(), key: 'identity', type: 'IDENTITY_VERIFICATION', label: 'Verify your identity',
        helpText: 'Scan your passport or ID — it takes about a minute', size: 12, blocking: true},
      {id: nid(), key: 'card_authorization', type: 'PAYMENT', paymentType: 'AUTHORIZATION_FEE',
        label: 'Confirm your card', helpText: 'A small amount is charged to verify your card',
        size: 12, blocking: true},
      {id: nid(), key: 'sign_offer', type: 'SIGNATURE', label: 'Sign your rental offer',
        helpText: 'Read and sign — you get a copy by email', size: 12, blocking: false},
    ],
  },
  {
    id: nid(), title: 'Your arrival', icon: 'ClockIcon',
    subtitle: 'Tell us when and how you arrive',
    questions: [
      {id: nid(), key: 'arrival_time', type: 'TIME', label: 'What time do you plan to arrive?',
        size: 6, required: false},
      {id: nid(), key: 'arrival_transport', type: 'RADIO_CARDS', label: 'How will you get here?',
        size: 12, required: false, options: [
          {id: nid(), value: 'car', label: 'By car', icon: 'TruckIcon', amount: 1},
          {id: nid(), value: 'train', label: 'By train', icon: 'NavigationIcon', amount: 1},
          {id: nid(), value: 'plane', label: 'By plane', icon: 'SendIcon', amount: 1},
          {id: nid(), value: 'other', label: 'Other', icon: 'MoreHorizontalIcon', amount: 1, freeText: true},
        ]},
      {id: nid(), key: 'flight_number', type: 'SHORT_TEXT', label: 'Flight number', size: 6,
        condition: {questionKey: 'arrival_transport', operator: 'EQUALS', value: 'plane'}},
    ],
  },
  {
    id: nid(), title: 'Make it special', icon: 'GiftIcon',
    subtitle: 'Optional extras for your stay',
    questions: [
      {id: nid(), key: 'extras', type: 'PRODUCT_PICKER', label: 'Would you like to add anything?',
        size: 12, multipleProducts: true, amountAllowed: true},
      {id: nid(), key: 'special_requests', type: 'LONG_TEXT', label: 'Anything else we should know?',
        placeholder: 'Allergies, celebrations, arrival with pets...', size: 12},
    ],
  },
];

export default {
  name: 'JourneyBuilder',
  components: {
    JourneyStepRail, JourneyQuestionEditor, JourneyConditionEditor, JourneyTargetPicker, JourneyPreview, InfoHint
  },
  data() {
    return {
      journey: emptyJourney(),
      selected: null,
      saving: false,
      features: [],
      conflicts: [],
      usedTemplate: false,
      // branch the journey's target resolves to (null = no branch restriction)
      targetBranchId: null,
      targetBranchSeq: 0,
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('appConfig', ['isRTL']),
    isNew() {
      return this.$route.params.id === 'new';
    },
    /**
     * Only products the target branch can actually deliver: the guest site
     * enforces branch membership at offer and order time, so showing others
     * here would let the author build a picker that is empty for guests.
     * Features WITHOUT any branch assignment are hidden too — hasBranch()
     * rejects them on the guest side, so offering them is a dead end.
     */
    filteredFeatures() {
      if (this.targetBranchId == null) return this.features;
      const branchId = Number(this.targetBranchId);
      return this.features.filter(feature =>
          Array.isArray(feature.branchIds) && feature.branchIds.some(id => Number(id) === branchId));
    },
    settings() {
      if (!this.journey.settings) this.$set(this.journey, 'settings', {});
      return this.journey.settings;
    },
    target: {
      get() {
        return {
          scope: this.journey.scope,
          branchId: this.journey.branchId,
          buildingId: this.journey.buildingId,
          roomCategoryId: this.journey.roomCategoryId,
          realEstateId: this.journey.realEstateId,
          priority: this.journey.priority,
        };
      },
      set(target) {
        Object.keys(target).forEach(key => this.$set(this.journey, key, target[key]));
      },
    },
    allQuestions() {
      return (this.journey.steps || []).reduce((list, step) => list.concat(step.questions || []), []);
    },
    duplicateKeys() {
      const seen = {};
      const duplicates = [];
      this.allQuestions.forEach(question => {
        const key = (question.key || '').toLowerCase();
        if (!key) return;
        if (seen[key] && !duplicates.includes(question.key)) duplicates.push(question.key);
        seen[key] = true;
      });
      return duplicates;
    },
    canSave() {
      return !!(this.journey.name || '').trim() && this.duplicateKeys.length === 0;
    },
    selectedStep() {
      if (!this.selected) return null;
      if (this.selected.type === 'step')
        return (this.journey.steps || []).find(step => step.id === this.selected.id) || null;
      return (this.journey.steps || [])[this.selected.stepIndex] || null;
    },
    selectedQuestion() {
      if (!this.selected || this.selected.type !== 'question') return null;
      return this.allQuestions.find(question => question.id === this.selected.id) || null;
    },
    selectedTitle() {
      if (!this.selected) return this.msg('Settings');
      return this.selected.type === 'step' ? this.msg('step') : this.msg('question');
    },
    focusStepId() {
      return this.selectedStep ? this.selectedStep.id : null;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onEscape);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onEscape);
  },
  created() {
    this.loadFeatures();
    if (this.isNew) {
      // new journeys start from the standard pre-arrival template
      this.journey.name = this.msg('starterjourneyname');
      this.journey.steps = starterSteps();
      this.usedTemplate = true;
    } else {
      this.load();
    }
  },
  watch: {
    // the product list follows the journey's target
    'journey.scope': 'resolveTargetBranch',
    'journey.branchId': 'resolveTargetBranch',
    'journey.buildingId': 'resolveTargetBranch',
    'journey.realEstateId': 'resolveTargetBranch',
  },
  methods: {
    /**
     * Escape deselects. Ignored while the guest is typing in the editor —
     * closing the panel out from under a half-finished field would lose the
     * edit — and while a modal is open, which owns Escape itself.
     */
    onEscape(event) {
      if (event.key !== 'Escape' || !this.selected) return;
      const tag = (event.target && event.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].indexOf(tag) >= 0) return;
      if (document.querySelector('.modal.show')) return;
      this.selected = null;
    },
    load() {
      const $this = this;
      this.$store.dispatch('journey/get', this.$route.params.id)
          .then(journey => {
            if (!journey) return;
            $this.journey = {...emptyJourney(), ...journey};
            $this.loadConflicts();
            $this.resolveTargetBranch();
          })
          .catch(() => $this.$router.push('/journey'));
    },
    /**
     * Which branch does the journey's target belong to?
     * BRANCH is direct; BUILDING and ROOM resolve through their entity's buid;
     * ORGANIZATION and ROOM_CATEGORY don't restrict (categories are org-wide).
     * The sequence counter drops stale async answers when the target changes
     * faster than the lookups resolve.
     */
    resolveTargetBranch() {
      const $this = this;
      const seq = ++this.targetBranchSeq;
      const journey = this.journey;

      if (journey.scope === 'BRANCH') {
        this.targetBranchId = journey.branchId || null;
        return;
      }
      if (journey.scope === 'BUILDING' && journey.buildingId) {
        this.$store.dispatch('building/getBuildingById', journey.buildingId)
            .then(building => {
              if (seq === $this.targetBranchSeq) $this.targetBranchId = (building && building.buid) || null;
            })
            .catch(() => {
              if (seq === $this.targetBranchSeq) $this.targetBranchId = null;
            });
        return;
      }
      if (journey.scope === 'ROOM' && journey.realEstateId) {
        this.$store.dispatch('realEstate/getRealEstateById', journey.realEstateId)
            .then(room => {
              if (seq === $this.targetBranchSeq) $this.targetBranchId = (room && room.buid) || null;
            })
            .catch(() => {
              if (seq === $this.targetBranchSeq) $this.targetBranchId = null;
            });
        return;
      }
      this.targetBranchId = null;
    },
    startBlank() {
      // even a blank journey keeps the mandatory details step
      this.journey.steps = [{
        id: 'new_' + Date.now().toString(36),
        title: this.msg('yourdetails') || 'Your details',
        questions: [{
          id: 'new_' + Date.now().toString(36) + 'q',
          key: 'guest_details', type: 'GUEST_DETAILS',
          label: 'Confirm your contact details', size: 12, blocking: true,
        }],
      }];
      this.selected = null;
      this.usedTemplate = false;
    },
    loadFeatures() {
      const $this = this;
      this.$store.dispatch('guestFeature/fetchAllGuestFeature', {page: 1, amount: 500, filter: {}})
          .then(ans => {
            // the endpoint returns the list under "Features"
            const features = ans && ans['Features'];
            $this.features = Array.isArray(features) ? features : [];
          })
          .catch(() => {
            $this.features = [];
          });
    },
    loadConflicts() {
      const $this = this;
      if (this.isNew) return;
      this.$store.dispatch('journey/getConflicts', this.journey.id)
          .then(list => $this.conflicts = list || []);
    },
    questionsBefore(selected) {
      // only earlier questions may be referenced by a condition
      const list = [];
      let stop = false;
      (this.journey.steps || []).forEach(step => {
        if (stop) return;
        if (selected.type === 'step' && step.id === selected.id) {
          stop = true;
          return;
        }
        (step.questions || []).forEach(question => {
          if (stop) return;
          if (selected.type === 'question' && question.id === selected.id) {
            stop = true;
            return;
          }
          if (question.key) list.push({key: question.key, label: question.label || question.key});
        });
      });
      return list;
    },
    patchStep(patch) {
      const index = (this.journey.steps || []).findIndex(step => step.id === this.selectedStep.id);
      if (index < 0) return;
      this.$set(this.journey.steps, index, {...this.journey.steps[index], ...patch});
    },
    patchQuestion(question) {
      const stepIndex = (this.journey.steps || [])
          .findIndex(step => (step.questions || []).some(item => item.id === question.id));
      if (stepIndex < 0) return;
      const questions = [...(this.journey.steps[stepIndex].questions || [])];
      const index = questions.findIndex(item => item.id === question.id);
      questions[index] = question;
      this.$set(this.journey.steps, stepIndex, {...this.journey.steps[stepIndex], questions});
    },
    /**
     * "Really picked?" checks before anything is stored: an auto-selecting
     * picker without explicit products, or products the target branch cannot
     * deliver, would only surface as a broken guest experience later.
     */
    validateBeforeSave() {
      const errors = [];
      const availableIds = new Set(this.filteredFeatures.map(feature => feature.id));
      this.allQuestions
          .filter(question => question.type === 'PRODUCT_PICKER')
          .forEach(question => {
            const name = question.label || question.key;
            const ids = question.guestFeatureIds || [];
            if (question.autoSelect === true && ids.length === 0)
              errors.push(this.msg('autoselectneedsproducts') + ': ' + name);
            if (ids.some(id => !availableIds.has(id)))
              errors.push(this.msg('productsnotforbranch') + ': ' + name);
          });
      // the details step is mandatory — the backend refuses to save without it
      const hasDetails = this.allQuestions.some(question => question.type === 'GUEST_DETAILS');
      if (!hasDetails)
        errors.push(this.msg('detailssteprequired'));
      // products linked through options must exist for the branch too
      this.allQuestions.forEach(question => {
        const missing = (question.options || [])
            .some(option => option.guestFeatureId && !availableIds.has(option.guestFeatureId));
        if (missing)
          errors.push(this.msg('productsnotforbranch') + ': ' + (question.label || question.key));
      });
      return errors;
    },
    save() {
      const $this = this;
      const errors = this.validateBeforeSave();
      if (errors.length) {
        this.$store.dispatch('notifications/error',
            {title: this.msg('guestjourney'), message: errors.join('\n')});
        return;
      }
      this.saving = true;
      const action = this.isNew ? 'journey/add' : 'journey/update';
      this.$store.dispatch(action, this.journey)
          .then(saved => {
            if (!saved) return;
            $this.journey = {...emptyJourney(), ...saved};
            if ($this.isNew) $this.$router.replace('/journey/edit/' + saved.id);
            else $this.loadConflicts();
          })
          .finally(() => $this.saving = false);
    },
  }
}
</script>

<style lang="scss" scoped>
.jb-head {
  padding: 12px 16px;
  margin-bottom: 14px;

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    &--target {
      margin-top: 10px;
      align-items: flex-start;
    }
  }

  &__name {
    flex: 1;
    min-width: 200px;
    font-weight: 600;
  }

  &__active {
    white-space: nowrap;
  }

  &__target {
    flex: 1;
    min-width: 280px;
  }
}

.jb-alert {
  margin: 10px 0 0;
  padding: 8px 12px;
}

.jb-pane {
  height: 100%;

  &__title {
    padding: 10px 14px;
    border-bottom: 1px solid rgba(34, 41, 47, .08);
    font-weight: 700;
    color: #5e5873;

    /* the editor pane's header also carries the way back to the settings */
    &--bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      /* keeps the row the same height as the plain title next to it, so the
         two panes' headers stay aligned when no button is showing */
      min-height: 42px;
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }

  .jb-back {
    font-weight: 600;
    white-space: nowrap;
  }

  &__body {
    padding: 14px;
    max-height: calc(100vh - 260px);
    overflow-y: auto;
  }
}

.jb-settings-title {
  font-weight: 700;
  color: #7367f0;
  margin: 4px 0 8px;
  font-size: 12.5px;
  text-transform: uppercase;
  letter-spacing: .03em;
}

.dark-layout {
  .jb-pane__title {
    color: #d0d2d6;
    border-bottom-color: rgba(255, 255, 255, .08);
  }
}
</style>
